import type * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAuth } from '@/auth/AuthProvider';
import type { AdminAuthStatus } from '@/auth/authStatus';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/restricted';

type Props = {
  // Aceita só as variantes "rejeitada" — AUTHORIZED/SIGNED_OUT/LOADING
  // não chegam aqui (router cuida disso antes).
  status: Extract<
    AdminAuthStatus,
    { kind: 'WRONG_DOMAIN' } | { kind: 'EMAIL_NOT_VERIFIED' } | { kind: 'MISSING_CLAIM' }
  >;
};

/**
 * Tela "Acesso restrito": explica por que a conta foi recusada e encerra a
 * sessão sozinha.
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2, Google SSO
 * restrito + guard de superAdmin).
 *
 * Por quê: uma sessão recusada (domínio errado, e-mail não verificado, sem
 * claim `superAdmin`) nunca é segura de manter — o token continuaria válido
 * em segundo plano. Por isso o `signOut()` roda no mount; a tela só mostra o
 * motivo até ele completar, e o botão acelera.
 *
 * Contrato: recebe só as variantes de recusa de `AdminAuthStatus`; mostra a
 * mensagem i18n da variante e o e-mail. Efeito colateral: `signOut()` (falha
 * vai para `console.error`).
 *
 * Usado por: `auth/RequireSuperAdmin.tsx` (rotas autenticadas) e
 * `routes/Login.tsx` (recusa logo após o popup).
 */
export function RestrictedAccess({ status }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  // Auto-signOut: a sessão atual nunca é segura de manter para variantes
  // rejeitadas (token continuaria válido em background). Tela apenas mostra
  // o motivo até o auto-signOut completar; usuário pode acelerar pelo botão.
  useEffect(() => {
    console.warn(`${TAG}: rejecting session reason=${status.kind} email=${status.email}`);
    void signOut().catch((err: unknown) => {
      console.error(`${TAG}: signOut failed`, err);
      // #1021 (2026-10-23): o auto-signOut protege contra manter um token de sessão
      // rejeitada; se falhar o token continua válido em background e só havia console.
      reportNonFatal('admin_restricted_signout_failed', {
        reason: status.kind,
        code: (err as { code?: string }).code ?? null,
        ...serializeError(err),
      });
    });
  }, [status.kind, status.email, signOut]);

  const messageKey: string = ((): string => {
    switch (status.kind) {
      case 'WRONG_DOMAIN':
        return 'auth.restricted.wrongDomain';
      case 'EMAIL_NOT_VERIFIED':
        return 'auth.restricted.emailNotVerified';
      case 'MISSING_CLAIM':
        return 'auth.restricted.missingClaim';
    }
  })();

  return (
    <main
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: '0 16px',
        textAlign: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: 448 }}>
        <SpaceBetween size="m">
          <Box variant="h1">{t('auth.restricted.title')}</Box>
          <Box color="text-body-secondary">{t(messageKey)}</Box>
          <Box fontSize="body-s" color="text-status-inactive">
            {status.email}
          </Box>
          <Button
            onClick={(): void => {
              void signOut();
            }}
          >
            {t('auth.restricted.signOutAndRetry')}
          </Button>
        </SpaceBetween>
      </div>
    </main>
  );
}
